import React, { useState, useEffect, useMemo } from 'react';
import { api, useAuth } from '../App';
import Modal from '../components/UI/Modal';
import {
  UserPlus, Search, Pencil, UserX, UserCheck, Key,
  MapPin, Clock, Users2, Check, AlertCircle, Eye, EyeOff
} from 'lucide-react';

const ROLES = {
  admin:    { label: 'Admin',    color: '#C9A84C', bg: 'rgba(201,168,76,.10)', desc: 'Full system access', icon: '★' },
  manager:  { label: 'Manager',  color: '#4A9EBF', bg: 'rgba(74,158,191,.10)', desc: 'Location management', icon: '◆' },
  staff:    { label: 'Staff',    color: '#5BC4C0', bg: 'rgba(91,196,192,.10)', desc: 'Inventory operations', icon: '●' },
  supplier: { label: 'Supplier', color: '#B07FD4', bg: 'rgba(176,127,212,.10)', desc: 'Portal access', icon: '▲' },
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [modal, setModal] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', locations: [], dashboard_preference: '' });
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [locFilter, setLocFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const load = () => api('/api/users').then(r => setUsers(r.data)).catch(console.error);
  useEffect(() => { load(); api('/api/locations').then(r => setLocations(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    let list = [...users];
    if (search) { const q = search.toLowerCase(); list = list.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)); }
    if (roleFilter) list = list.filter(u => u.role === roleFilter);
    if (statusFilter === 'active') list = list.filter(u => u.is_active);
    else if (statusFilter === 'inactive') list = list.filter(u => !u.is_active);
    if (locFilter) list = list.filter(u => (u.locations || []).includes(parseInt(locFilter)));
    list.sort((a, b) => {
      switch (sortBy) {
        case 'name': return a.name.localeCompare(b.name);
        case 'role': { const o = { admin:0, manager:1, staff:2, supplier:3 }; return (o[a.role]??9) - (o[b.role]??9); }
        case 'login': return (new Date(b.last_login || 0)) - (new Date(a.last_login || 0));
        case 'recent': return (new Date(b.created_at || 0)) - (new Date(a.created_at || 0));
        default: return 0;
      }
    });
    return list;
  }, [users, search, roleFilter, statusFilter, locFilter, sortBy]);

  const counts = useMemo(() => {
    const c = { total: users.length, active: 0 };
    Object.keys(ROLES).forEach(r => c[r] = 0);
    users.forEach(u => { if (u.is_active) c.active++; c[u.role] = (c[u.role] || 0) + 1; });
    return c;
  }, [users]);

  const toggleLoc = (id) => setForm(p => ({ ...p, locations: p.locations.includes(id) ? p.locations.filter(x => x !== id) : [...p.locations, id] }));
  const openAdd = () => { setForm({ name:'', email:'', password:'', role:'staff', locations:[], dashboard_preference:'' }); setEditUser(null); setShowPw(false); setModal('add'); };
  const openEdit = (u) => { setForm({ name:u.name, email:u.email, password:'', role:u.role, locations:u.locations||[], dashboard_preference:u.dashboard_preference||'' }); setEditUser(u); setShowPw(false); setModal('edit'); };
  const openResetPw = (u) => { setResetPwUser(u); setResetPw(''); setShowPw(false); setModal('resetPw'); };

  const submit = async () => {
    try {
      if (modal === 'edit') { const b = {...form}; if (!b.password) delete b.password; await api('/api/users/'+editUser.id, { method:'PUT', body:JSON.stringify(b) }); }
      else await api('/api/users', { method:'POST', body:JSON.stringify(form) });
      setModal(null); load();
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const submitResetPw = async () => {
    if (!resetPw || resetPw.length < 8) return alert('Min 8 characters');
    try { await api('/api/users/'+resetPwUser.id, { method:'PUT', body:JSON.stringify({ password:resetPw }) }); setModal(null); } catch (err) { alert(err.error || 'Failed'); }
  };

  const toggleActive = async (u) => {
    if (!confirm((u.is_active ? 'Deactivate' : 'Reactivate') + ' ' + u.name + '?')) return;
    if (u.is_active) await api('/api/users/'+u.id, { method:'DELETE' });
    else await api('/api/users/'+u.id, { method:'PUT', body:JSON.stringify({ is_active:1 }) });
    load();
  };

  const ini = (n) => (n||'U').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const locName = (id) => locations.find(l=>l.id===id)?.name || '?';

  const hasFilters = search || roleFilter || statusFilter || locFilter;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontFamily:'Lora,serif', fontSize:28, marginBottom:4 }}>Users</h1>
          <p style={{ fontSize:13, color:'var(--muted)' }}>{counts.active} active of {counts.total} total</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><UserPlus size={16} /> Add User</button>
      </div>

      {/* Role summary pills */}
      <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
        <RolePill label="All" count={counts.total} active={!roleFilter} onClick={() => setRoleFilter('')} color="var(--gold)" />
        {Object.entries(ROLES).map(([key, r]) => (
          <RolePill key={key} label={r.label} count={counts[key]||0} active={roleFilter===key} onClick={() => setRoleFilter(roleFilter===key ? '' : key)} color={r.color} />
        ))}
      </div>

      {/* Search + filters */}
      <div style={{ display:'flex', gap:10, marginBottom:24, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative', flex:'1 1 220px', minWidth:180 }}>
          <Search size={15} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--faint)' }} />
          <input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft:36, width:'100%' }} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width:'auto', minWidth:120 }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={locFilter} onChange={e => setLocFilter(e.target.value)} style={{ width:'auto', minWidth:150 }}>
          <option value="">All Locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ width:'auto', minWidth:150 }}>
          <option value="name">Sort: Name A-Z</option>
          <option value="role">Sort: Role</option>
          <option value="login">Sort: Last Login</option>
          <option value="recent">Sort: Recently Added</option>
        </select>
        {hasFilters && <button onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter(''); setLocFilter(''); }} style={{ background:'none', border:'none', color:'var(--gold)', fontSize:13, cursor:'pointer', fontWeight:600, fontFamily:'DM Sans,sans-serif' }}>Clear</button>}
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'70px 20px' }}>
          <div style={{ width:60, height:60, borderRadius:16, background:'var(--card)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
            <Users2 size={26} style={{ color:'var(--faint)', opacity:.4 }} />
          </div>
          <p style={{ fontSize:17, fontWeight:600, marginBottom:6 }}>{hasFilters ? 'No users match' : 'No users yet'}</p>
          <p style={{ fontSize:13, color:'var(--faint)', maxWidth:300, margin:'0 auto' }}>{hasFilters ? 'Adjust your search or filters.' : 'Add your first team member to get started.'}</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(380px, 1fr))', gap:18 }}>
          {filtered.map(u => {
            const r = ROLES[u.role] || ROLES.staff;
            const isMe = me?.id === u.id;
            const allLocs = u.locations?.length === locations.length && locations.length > 0;
            return (
              <div key={u.id} style={{
                background:'var(--card)', border:'1px solid var(--border)', borderRadius:16,
                overflow:'hidden', transition:'all .15s', opacity: u.is_active ? 1 : .55,
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow='0 8px 24px var(--shadow)'; e.currentTarget.style.borderColor=r.color+'40'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor='var(--border)'; }}
              >
                {/* Header */}
                <div style={{ padding:'20px 22px 16px', display:'flex', gap:14 }}>
                  <div style={{
                    width:50, height:50, borderRadius:14, background: r.bg,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontFamily:'Lora,serif', fontSize:18, fontWeight:700, color: r.color, flexShrink:0,
                    border:'2px solid '+r.color+'25',
                  }}>{ini(u.name)}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                      <span style={{ fontWeight:700, fontSize:16 }}>{u.name}</span>
                      {isMe && <span style={{ fontSize:9, color:'var(--navy)', background:'var(--gold)', padding:'1px 7px', borderRadius:8, fontWeight:700 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize:13, color:'var(--muted)', marginBottom:8 }}>{u.email}</div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <span style={{ padding:'3px 11px', borderRadius:10, fontSize:10, fontWeight:700, letterSpacing:'.5px', background:r.bg, color:r.color, border:'1px solid '+r.color+'20' }}>{r.label.toUpperCase()}</span>
                      <span style={{
                        padding:'3px 10px', borderRadius:10, fontSize:10, fontWeight:600,
                        background: u.is_active ? 'rgba(46,204,113,.10)' : 'rgba(231,76,60,.10)',
                        color: u.is_active ? 'var(--success)' : 'var(--danger)',
                        border:'1px solid '+(u.is_active ? 'rgba(46,204,113,.2)' : 'rgba(231,76,60,.2)'),
                      }}>{u.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                    </div>
                  </div>
                </div>

                {/* Locations */}
                <div style={{ padding:'0 22px 16px' }}>
                  <div style={{ fontSize:10, color:'var(--faint)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:7, fontWeight:600 }}>Location Access</div>
                  {(u.locations && u.locations.length > 0) ? (
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      {allLocs ? (
                        <span style={{ padding:'4px 12px', borderRadius:8, fontSize:11, fontWeight:600, background:'var(--gold-pale)', color:'var(--gold)', border:'1px solid var(--gold)20' }}>
                          <MapPin size={10} style={{ verticalAlign:'-1px', marginRight:4 }} />All {locations.length} locations
                        </span>
                      ) : u.locations.map(lid => (
                        <span key={lid} style={{ padding:'4px 10px', borderRadius:8, fontSize:11, background:'var(--navy)', color:'var(--muted)', border:'1px solid var(--border)' }}>
                          {locName(lid)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize:12, color:'var(--faint)', fontStyle:'italic' }}>No locations assigned</span>
                  )}
                </div>

                {/* Footer */}
                <div style={{ padding:'12px 22px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:11, color:'var(--faint)', display:'flex', alignItems:'center', gap:5 }}>
                    <Clock size={11} />
                    {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never logged in'}
                  </span>
                  <div style={{ display:'flex', gap:5 }}>
                    <ActionBtn icon={Pencil} color="var(--gold)" tip="Edit" onClick={() => openEdit(u)} />
                    <ActionBtn icon={Key} color="#78A8E8" tip="Reset Password" onClick={() => openResetPw(u)} />
                    {!isMe && <ActionBtn icon={u.is_active ? UserX : UserCheck} color={u.is_active ? 'var(--danger)' : 'var(--success)'} tip={u.is_active ? 'Deactivate' : 'Reactivate'} onClick={() => toggleActive(u)} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit */}
      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'edit' ? 'Edit User' : 'Create User'} onClose={() => setModal(null)}>
          <div className="grid-2">
            <div className="form-group"><label>Full Name</label><input value={form.name} onChange={e => setForm({...form, name:e.target.value})} required /></div>
            <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} required /></div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>{modal === 'edit' ? 'New Password (optional)' : 'Password'}</label>
              <div style={{ position:'relative' }}>
                <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({...form, password:e.target.value})} placeholder={modal === 'edit' ? 'Leave blank to keep' : 'Min 8 characters'} required={modal === 'add'} style={{ paddingRight:38 }} />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--faint)', padding:2 }}>{showPw ? <EyeOff size={15} /> : <Eye size={15} />}</button>
              </div>
            </div>
            <div className="form-group"><label>Role</label>
              <select value={form.role} onChange={e => setForm({...form, role:e.target.value})}>
                {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.desc}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label>Default Dashboard</label>
            <select value={form.dashboard_preference || ''} onChange={e => setForm({...form, dashboard_preference: e.target.value || null})}>
              <option value="">Auto (based on role)</option>
              <option value="command-center">Command Center</option>
              <option value="operations">Operations</option>
            </select>
          </div>
          <div className="form-group">
            <label>Location Access</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:6 }}>
              <LocChip label="All Locations" checked={form.locations.length === locations.length && locations.length > 0}
                onChange={() => setForm(p => ({...p, locations: p.locations.length === locations.length ? [] : locations.map(l=>l.id)}))}
                bold color="var(--gold)" />
              {locations.map(l => <LocChip key={l.id} label={l.name} checked={form.locations.includes(l.id)} onChange={() => toggleLoc(l.id)} />)}
            </div>
          </div>
          {/* TODO: Add invite-by-email flow as alternative to password creation */}
          {/* TODO: Add permissions granularity beyond role when needed */}
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>{modal === 'edit' ? 'Save Changes' : 'Create User'}</button>
          </div>
        </Modal>
      )}

      {/* Reset Password */}
      {modal === 'resetPw' && resetPwUser && (
        <Modal title="Reset Password" onClose={() => setModal(null)}>
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', background:'var(--navy)', borderRadius:12, border:'1px solid var(--border)', marginBottom:18 }}>
            <div style={{ width:40, height:40, borderRadius:10, background:ROLES[resetPwUser.role]?.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Lora,serif', fontWeight:700, color:ROLES[resetPwUser.role]?.color, fontSize:15 }}>{ini(resetPwUser.name)}</div>
            <div><div style={{ fontWeight:600, fontSize:14 }}>{resetPwUser.name}</div><div style={{ fontSize:12, color:'var(--muted)' }}>{resetPwUser.email}</div></div>
          </div>
          <div className="form-group">
            <label>New Password</label>
            <div style={{ position:'relative' }}>
              <input type={showPw ? 'text' : 'password'} value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Min 8 characters" style={{ paddingRight:38 }} />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--faint)', padding:2 }}>{showPw ? <EyeOff size={15} /> : <Eye size={15} />}</button>
            </div>
            {resetPw && <div style={{ fontSize:11, marginTop:4, display:'flex', alignItems:'center', gap:4, color:resetPw.length >= 8 ? 'var(--success)' : 'var(--danger)' }}>{resetPw.length >= 8 ? <Check size={11} /> : <AlertCircle size={11} />} {resetPw.length >= 8 ? 'Ready' : 'Too short'}</div>}
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitResetPw} disabled={!resetPw || resetPw.length < 8}>Reset Password</button>
          </div>
        </Modal>
      )}

      <style>{`@media(max-width:600px){[style*="repeat(auto-fill"]{grid-template-columns:1fr !important;}}`}</style>
    </div>
  );
}

function RolePill({ label, count, active, onClick, color }) {
  return (
    <button onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:6, padding:'7px 14px',
      borderRadius:10, border:'1px solid '+(active ? color : 'var(--border)'),
      background: active ? color+'14' : 'var(--card)',
      color: active ? color : 'var(--muted)',
      cursor:'pointer', fontSize:13, fontWeight: active ? 600 : 400,
      fontFamily:'DM Sans,sans-serif', transition:'all .12s',
    }}
      onMouseEnter={e => { if(!active) { e.currentTarget.style.borderColor=color+'50'; e.currentTarget.style.color=color; }}}
      onMouseLeave={e => { if(!active) { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--muted)'; }}}
    >
      {label} <span style={{ fontSize:11, opacity:.7 }}>{count}</span>
    </button>
  );
}

function LocChip({ label, checked, onChange, bold, color }) {
  return (
    <label style={{
      display:'flex', alignItems:'center', gap:6, padding:'7px 14px',
      background: checked ? (color||'var(--gold)')+'0E' : 'var(--card)',
      border:'1px solid '+(checked ? (color||'var(--gold)') : 'var(--border)'),
      borderRadius:10, cursor:'pointer', fontSize:13, fontWeight: bold ? 600 : 400,
      transition:'all .12s',
    }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ width:'auto', accentColor: color||'var(--gold)' }} />
      {label}
    </label>
  );
}

function ActionBtn({ icon: Icon, color, tip, onClick }) {
  return (
    <button onClick={onClick} title={tip} style={{
      display:'inline-flex', alignItems:'center', justifyContent:'center', width:32, height:32,
      background:color+'0E', border:'1px solid '+color+'20', borderRadius:8,
      color, cursor:'pointer', transition:'all .12s', padding:0,
    }}
      onMouseEnter={e => { e.currentTarget.style.background=color+'22'; e.currentTarget.style.borderColor=color; e.currentTarget.style.transform='translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.background=color+'0E'; e.currentTarget.style.borderColor=color+'20'; e.currentTarget.style.transform='none'; }}
    >
      <Icon size={14} />
    </button>
  );
}
